import { api } from '../../lib/api.js';
import { BaseComponent, html, nothing, renderMarkdown } from '../../lib/lit.js';
import { MARKDOWN_STYLES } from '../../lib/markdown-styles.js';
import {
    answersPayload,
    emptySelection,
    MAX_ANSWER_LENGTH,
    pickNotListed,
    pickOption,
    promptSummary,
    selectionsFromResponse,
    tallyRows,
    validateSelections,
} from '../../lib/polls.js';
import { router } from '../../lib/router.js';
import { formatItemDate } from './community-item-list.js';
import { formatPollTime } from './community-polls.js';

/**
 * <community-poll-page> — one poll: answer it, and see its results when the
 * poll says you may.
 *
 * Results are only ever shown to people who answered — straight away, or once
 * the poll closes, as staff chose. Someone who did not answer never sees the
 * tallies, and nobody sees anyone else's written answers: those are for staff,
 * which is why a text question here has no image upload.
 *
 * Fires `poll-answered` after a response is saved, so the app can refresh its
 * list.
 */
export class CommunityPollPage extends BaseComponent {
    static styles = MARKDOWN_STYLES;

    static properties = {
        pollId: { type: String },
        _poll: { state: true },
        _response: { state: true },
        _canRespond: { state: true },
        _canChange: { state: true },
        _resultsAvailable: { state: true },
        _results: { state: true },
        _selections: { state: true },
        _editing: { state: true },
        _loading: { state: true },
        _submitting: { state: true },
        _error: { state: true },
    };

    constructor() {
        super();
        this.pollId = '';
        /** @type {object|null} */
        this._poll = null;
        /** @type {object|null} */
        this._response = null;
        this._canRespond = false;
        this._canChange = false;
        this._resultsAvailable = false;
        /** @type {object|null} */
        this._results = null;
        /** @type {Record<string, ReturnType<typeof emptySelection>>} */
        this._selections = {};
        this._editing = false;
        this._loading = true;
        this._submitting = false;
        this._error = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    updated(changed) {
        if (changed.has('pollId') && changed.get('pollId') !== undefined) this._load();
    }

    async _load() {
        if (!this.pollId) return;
        this._loading = true;
        this._error = '';
        this._results = null;
        try {
            this._apply(await api.get(`/polls/${this.pollId}`));
            this._editing = this._canRespond;
            if (this._resultsAvailable) await this._loadResults();
        } catch (err) {
            this._error =
                /** @type {any} */ (err).status === 404
                    ? 'That poll does not exist.'
                    : String(/** @type {any} */ (err).message ?? err);
        } finally {
            this._loading = false;
        }
    }

    /** Take in a poll as the API describes it to this member. */
    _apply(body) {
        this._poll = body.poll;
        this._response = body.response ?? null;
        this._canRespond = body.canRespond === true;
        this._canChange = body.canChange === true;
        this._resultsAvailable = body.resultsAvailable === true;
        this._selections = selectionsFromResponse(this._response);
    }

    async _loadResults() {
        try {
            this._results = await api.get(`/polls/${this.pollId}/results`);
        } catch (err) {
            this._error = String(/** @type {any} */ (err).message ?? err);
        }
    }

    _selection(questionId) {
        return this._selections[questionId] ?? emptySelection();
    }

    _setSelection(questionId, selection) {
        this._selections = { ...this._selections, [questionId]: selection };
        this._error = '';
    }

    async _submit(event) {
        event.preventDefault();
        if (this._submitting) return;

        const problem = validateSelections(this._poll, this._selections);
        if (problem) {
            this._error = problem;
            return;
        }

        this._submitting = true;
        this._error = '';
        try {
            const body = await api.put(`/polls/${this.pollId}/response`, {
                answers: answersPayload(this._poll, this._selections),
            });
            this._apply(body);
            this._editing = false;
            this.dispatchEvent(new CustomEvent('poll-answered', { bubbles: true, composed: true }));
            if (this._resultsAvailable) await this._loadResults();
        } catch (err) {
            this._error = String(/** @type {any} */ (err).message ?? err);
        } finally {
            this._submitting = false;
        }
    }

    _startChanging() {
        this._selections = selectionsFromResponse(this._response);
        this._error = '';
        this._editing = true;
    }

    _cancelChanging() {
        this._selections = selectionsFromResponse(this._response);
        this._error = '';
        this._editing = false;
    }

    _renderChoice(question) {
        const selection = this._selection(question.id);
        const type = question.allowMultiple ? 'checkbox' : 'radio';
        const name = `q-${question.id}`;
        const disabled = !this._editing;

        return html`
            ${question.options.map(
                (option) => html`
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type=${type}
                            name=${name}
                            id="o-${option.id}"
                            .checked=${selection.optionIds.includes(option.id)}
                            ?disabled=${disabled}
                            @change=${() =>
                                this._setSelection(
                                    question.id,
                                    pickOption(selection, option.id, question.allowMultiple),
                                )}
                        />
                        <label class="form-check-label" for="o-${option.id}">${option.label}</label>
                    </div>
                `,
            )}
            ${
                question.allowNotListed
                    ? html`
                          <div class="form-check">
                              <input
                                  class="form-check-input"
                                  type=${type}
                                  name=${name}
                                  id="nl-${question.id}"
                                  .checked=${selection.notListed}
                                  ?disabled=${disabled}
                                  @change=${() =>
                                      this._setSelection(
                                          question.id,
                                          pickNotListed(selection, question.allowMultiple),
                                      )}
                              />
                              <label class="form-check-label" for="nl-${question.id}"
                                  >Not listed</label
                              >
                          </div>
                          ${
                              selection.notListed
                                  ? html`<input
                                        class="form-control form-control-sm mt-1 ms-4"
                                        style="max-width: 28rem;"
                                        placeholder="What's missing?"
                                        aria-label="What's not listed"
                                        maxlength=${MAX_ANSWER_LENGTH}
                                        .value=${selection.notListedText}
                                        ?disabled=${disabled}
                                        @input=${(e) =>
                                            this._setSelection(question.id, {
                                                ...this._selection(question.id),
                                                notListedText: e.target.value,
                                            })}
                                    />`
                                  : nothing
                          }
                      `
                    : nothing
            }
        `;
    }

    _renderText(question) {
        const selection = this._selection(question.id);
        return html`
            <textarea
                class="form-control"
                rows="3"
                aria-label="Your answer"
                maxlength=${MAX_ANSWER_LENGTH}
                .value=${selection.text}
                ?disabled=${!this._editing}
                @input=${(e) =>
                    this._setSelection(question.id, {
                        ...this._selection(question.id),
                        text: e.target.value,
                    })}
            ></textarea>
            <div class="form-text">Only the D3 team reads written answers.</div>
        `;
    }

    _renderQuestion(question, index) {
        return html`
            <div class="mb-4" role="group" aria-labelledby="p-${question.id}">
                <div id="p-${question.id}" class="fw-semibold mb-2 d-flex gap-2">
                    <span class="text-muted">${index + 1}.</span>
                    <div class="markdown-body flex-grow-1">${renderMarkdown(question.prompt)}</div>
                </div>
                ${
                    question.kind === 'choice' && question.allowMultiple
                        ? html`<div class="form-text mt-0 mb-1">Pick any that apply.</div>`
                        : nothing
                }
                ${question.kind === 'text' ? this._renderText(question) : this._renderChoice(question)}
            </div>
        `;
    }

    _renderActions() {
        if (this._editing) {
            const changing = this._response !== null;
            return html`
                <div class="d-flex gap-2 align-items-center flex-wrap">
                    <button class="btn btn-primary" type="submit" ?disabled=${this._submitting}>
                        ${
                            this._submitting
                                ? html`<span
                                          class="spinner-border spinner-border-sm me-2"
                                          role="status"
                                      ></span>Saving…`
                                : changing
                                  ? 'Save my answers'
                                  : 'Submit'
                        }
                    </button>
                    ${
                        changing
                            ? html`<button
                                  class="btn btn-outline-secondary"
                                  type="button"
                                  ?disabled=${this._submitting}
                                  @click=${this._cancelChanging}
                              >
                                  Cancel
                              </button>`
                            : nothing
                    }
                    ${
                        !changing && !this._poll.allowChanges
                            ? html`<span class="text-muted small"
                                  >You can't change your answers once you submit.</span
                              >`
                            : nothing
                    }
                </div>
            `;
        }
        if (this._canChange) {
            return html`
                <button class="btn btn-outline-primary" type="button" @click=${this._startChanging}>
                    <i class="bi bi-pencil me-1"></i>Change my answers
                </button>
            `;
        }
        return nothing;
    }

    _renderStatus() {
        const poll = this._poll;
        if (this._response) {
            return html`<div class="alert alert-success py-2 small mb-3">
                <i class="bi bi-check-circle me-1"></i>You answered this poll on
                ${formatItemDate(this._response.updatedAt ?? this._response.submittedAt)}.
            </div>`;
        }
        if (poll.state === 'closed') {
            return html`<div class="alert alert-secondary py-2 small mb-3">
                This poll closed before you answered it.
            </div>`;
        }
        return nothing;
    }

    _renderResultQuestion(question, result, index) {
        if (question.kind === 'text') {
            return html`
                <div class="mb-3">
                    <div class="small fw-semibold">${index + 1}. ${promptSummary(question.prompt)}</div>
                    <div class="text-muted small">
                        ${result.answered} wrote an answer. Written answers are read by the D3
                        team.
                    </div>
                </div>
            `;
        }

        return html`
            <div class="mb-3">
                <div class="small fw-semibold mb-1">${index + 1}. ${promptSummary(question.prompt)}</div>
                ${tallyRows(question, result).map(
                    (row) => html`
                        <div class="small d-flex justify-content-between">
                            <span class=${row.notListed ? 'fst-italic' : ''}>${row.label}</span>
                            <span class="text-muted">${row.count} · ${row.percent}%</span>
                        </div>
                        <div
                            class="progress mb-2"
                            style="height: 0.5rem;"
                            role="progressbar"
                            aria-label=${row.label}
                            aria-valuenow=${row.percent}
                            aria-valuemin="0"
                            aria-valuemax="100"
                        >
                            <div class="progress-bar" style="width: ${row.percent}%"></div>
                        </div>
                    `,
                )}
            </div>
        `;
    }

    _renderResults() {
        const poll = this._poll;

        if (this._resultsAvailable && this._results) {
            const byId = Object.fromEntries(this._results.questions.map((q) => [q.questionId, q]));
            return html`
                <div class="card shadow-sm border-0 mb-4">
                    <div class="card-body p-4">
                        <h2 class="h6 mb-1">Results</h2>
                        <div class="text-muted small mb-3">
                            ${this._results.respondents}
                            ${this._results.respondents === 1 ? 'person has' : 'people have'}
                            answered${poll.state === 'open' ? ' so far' : ''}.
                        </div>
                        ${poll.questions.map((question, index) =>
                            byId[question.id]
                                ? this._renderResultQuestion(question, byId[question.id], index)
                                : nothing,
                        )}
                    </div>
                </div>
            `;
        }

        let message = '';
        if (this._response && poll.resultsVisibility === 'at_close' && poll.state === 'open') {
            message = poll.closesAt
                ? `Results when this poll closes, ${formatPollTime(poll.closesAt)}.`
                : 'Results when this poll closes.';
        } else if (!this._response) {
            message = 'Results are shown to people who answered.';
        }
        if (!message) return nothing;

        return html`<p class="text-muted small"><i class="bi bi-bar-chart me-1"></i>${message}</p>`;
    }

    _renderMeta() {
        const poll = this._poll;
        const open = poll.state === 'open';
        return html`
            <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <i class="bi bi-bar-chart text-secondary"></i>
                <span class="text-muted small">Poll</span>
                ${
                    open
                        ? html`<span class="badge text-bg-success">Open</span>`
                        : html`<span class="badge text-bg-secondary">Closed</span>`
                }
                <span class="text-muted small">
                    ${
                        open
                            ? poll.closesAt
                                ? `Closes ${formatPollTime(poll.closesAt)}`
                                : nothing
                            : `Closed ${formatItemDate(poll.closedAt)}`
                    }
                </span>
            </div>
        `;
    }

    render() {
        if (this._loading) {
            return html`
                <div class="text-muted small d-flex align-items-center gap-2">
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                    Loading…
                </div>
            `;
        }

        const back = html`
            <button class="btn btn-link px-0 mb-2" @click=${() => router.navigate('/polls')}>
                <i class="bi bi-arrow-left me-1"></i>All polls
            </button>
        `;

        if (!this._poll) {
            return html`
                ${back}
                <div class="alert alert-warning" role="alert">${this._error}</div>
            `;
        }

        const poll = this._poll;
        return html`
            ${back}
            <div class="card shadow-sm border-0 mb-4">
                <div class="card-body p-4">
                    ${this._renderMeta()}
                    <h1 class="h4 mb-3">${poll.title}</h1>
                    ${
                        poll.intro
                            ? html`<div class="markdown-body mb-4">${renderMarkdown(poll.intro)}</div>`
                            : nothing
                    }
                    ${this._renderStatus()}
                    <form @submit=${this._submit}>
                        ${poll.questions.map((question, index) => this._renderQuestion(question, index))}
                        ${
                            this._error
                                ? html`<div class="alert alert-danger py-2">${this._error}</div>`
                                : nothing
                        }
                        ${this._renderActions()}
                    </form>
                </div>
            </div>
            ${this._renderResults()}
        `;
    }
}

customElements.define('community-poll-page', CommunityPollPage);
